/**
 * Chat Welcome Component
 *
 * Welcome screen shown when chat history is empty.
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import AiChatIcon from 'features/ui/app-layout/rail/ai-chat-icon';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CampaignIcon from '@mui/icons-material/Campaign';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import useAIChatStore from '../../../stores/ai-chat-store';
import { SuggestedAction } from '../../../types/ai-chat.types';
import { getUserInfo } from '../../../API/user';
import { getOwnCompanyData } from '../../../API/company';
import { getAllLinkedinAccounts } from '../../../API/linkedin-accounts';
import { dc } from '../design-tokens';

interface ChatWelcomeProps {
  onSuggestedAction: (message: string) => void;
  /** When set, the step boxes open the workspace side panel instead of a chat message. */
  onOpenSidePanel?: (panel: 'profile' | 'team' | 'license' | 'linkedin' | 'workhours' | 'limits' | 'signature' | 'blacklist') => void;
}

/** Fields that must be filled before the profile counts as complete (mirrors /dashboard/account). */
const PROFILE_FIELD_LABELS: Record<string, { de: string; en: string }> = {
  firstName: { de: 'Vorname', en: 'First name' },
  lastName: { de: 'Nachname', en: 'Last name' },
  email: { de: 'E-Mail-Adresse', en: 'Email address' },
  companyName: { de: 'Unternehmen', en: 'Company' },
  street: { de: 'Straße', en: 'Street' },
  postalCode: { de: 'PLZ', en: 'Postal code' },
  city: { de: 'Stadt', en: 'City' },
  country: { de: 'Land', en: 'Country' },
};

const ChatWelcome: React.FC<ChatWelcomeProps> = ({ onSuggestedAction, onOpenSidePanel }) => {
  const { classes } = useStyles();
  const { t, i18n } = useTranslation();
  const suggestedActions = useAIChatStore((state) => state.suggestedActions);
  const locale = i18n.language;
  const [missingProfileFields, setMissingProfileFields] = useState<string[] | null>(null);
  const [hasLinkedInAccount, setHasLinkedInAccount] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    const checkProfile = async () => {
      try {
        const [user, companyRes, accountsRes] = await Promise.all([
          getUserInfo(),
          getOwnCompanyData(),
          getAllLinkedinAccounts(),
        ]);
        if (cancelled) return;
        const accounts = accountsRes?.allLinkedInAccounts ?? [];
        setHasLinkedInAccount(Array.isArray(accounts) ? accounts.length > 0 : true);
        const company = companyRes?.data;
        // Rolle: Firmendaten dürfen nur Admins/Owner setzen — für Member zählen
        // nur die eigenen Felder als "fehlend" (den Rest ergänzt ein Admin).
        const membership = (user?.tenants || []).find(
          (m: { tenantId?: string }) => m.tenantId === company?._id
        );
        const role = membership?.role || 'member';
        const canEditCompany = role === 'admin' || role === 'owner';
        const companyKeys = ['companyName', 'street', 'postalCode', 'city', 'country'];
        const values: Record<string, string | undefined> = {
          firstName: user?.firstName,
          lastName: user?.lastName,
          email: user?.email,
          companyName: company?.name,
          street: company?.address?.addressLine1,
          postalCode: company?.address?.postalCode,
          city: company?.address?.city,
          country: company?.address?.country,
        };
        setMissingProfileFields(
          Object.keys(PROFILE_FIELD_LABELS)
            .filter((key) => canEditCompany || !companyKeys.includes(key))
            .filter((key) => !values[key]?.trim())
        );
      } catch {
        // On error, fall back to the normal welcome screen instead of blocking it.
        if (!cancelled) setMissingProfileFields([]);
      }
    };
    checkProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  const profileIncomplete = (missingProfileFields?.length ?? 0) > 0;

  const startProfileCompletion = () => {
    if (onOpenSidePanel) {
      onOpenSidePanel('profile');
      return;
    }
    onSuggestedAction(
      locale === 'de'
        ? 'Schritt 1: Bitte vervollständige meine Profil- und Firmendaten (Vorname, Nachname, E-Mail, Unternehmen, Straße, PLZ, Stadt, Land). Prüfe mit dem Profil-Vollständigkeitscheck, welche Angaben fehlen, frage mich nur nach den fehlenden Feldern und speichere sie.'
        : 'Step 1: Please complete my profile and company data (first name, last name, email, company, street, postal code, city, country). Run the profile completeness check, ask me only for the missing fields, and save them.'
    );
  };

  // Schritt 2: nach vollständigen Profildaten ist der nächste Schritt immer
  // das Verbinden eines LinkedIn-Accounts.
  const linkedInStepPending = !profileIncomplete && hasLinkedInAccount === false;

  const startLinkedInConnect = () => {
    if (onOpenSidePanel) {
      onOpenSidePanel('linkedin');
      return;
    }
    onSuggestedAction(
      locale === 'de'
        ? 'Schritt 2: Ich möchte meinen LinkedIn-Account verbinden. Bitte führe mich durch die Einrichtung.'
        : 'Step 2: I want to connect my LinkedIn account. Please guide me through the setup.'
    );
  };

  const getIcon = (iconName?: string) => {
    switch (iconName) {
      case 'person_add':
        return <PersonAddIcon />;
      case 'campaign':
        return <CampaignIcon />;
      case 'analytics':
        return <AnalyticsIcon />;
      case 'help':
        return <HelpOutlineIcon />;
      default:
        return <HelpOutlineIcon />;
    }
  };

  return (
    <Box className={classes.welcomeContainer}>
      <Box className={classes.logoContainer}>
        <Box className={classes.logoCircle}>
          <AiChatIcon className={classes.logoIcon} />
        </Box>
      </Box>

      <Typography variant="h5" className={classes.title}>
        {locale === 'de' ? 'Willkommen bei SAIA' : t('welcomeToPAIAssistant', 'Welcome to SAIA')}
      </Typography>

      <Typography variant="body2" color="textSecondary" className={classes.subtitle}>
        {locale === 'de'
          ? 'Ich helfe dir bei der Verwaltung deiner LinkedIn-Kampagnen, beim Hinzufügen von Accounts und beim Verstehen deiner Performance. Was möchtest du heute tun?'
          : t(
              'aiAssistantIntro',
              'I\'m here to help you manage LinkedIn campaigns, add accounts, and understand your performance metrics. What would you like to do today?'
            )}
      </Typography>

      {profileIncomplete && (
        <Paper className={classes.profileStepBox} elevation={0} onClick={startProfileCompletion}>
          <Box className={classes.profileStepHeader}>
            <AssignmentIndIcon className={classes.profileStepIcon} />
            <Typography variant="subtitle2" className={classes.profileStepTitle}>
              {locale === 'de'
                ? 'Schritt 1: Account-Daten anlegen'
                : t('profileStep1Title', 'Step 1: Set up your account details')}
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" className={classes.profileStepText}>
            {locale === 'de'
              ? `Es fehlen noch: ${missingProfileFields!
                  .map((key) => PROFILE_FIELD_LABELS[key].de)
                  .join(', ')}. Klicke hier und ich frage die fehlenden Angaben ab und trage sie ein.`
              : `Still missing: ${missingProfileFields!
                  .map((key) => PROFILE_FIELD_LABELS[key].en)
                  .join(', ')}. Click here and I will ask for the missing details and save them.`}
          </Typography>
        </Paper>
      )}

      {linkedInStepPending && (
        <Paper className={classes.profileStepBox} elevation={0} onClick={startLinkedInConnect}>
          <Box className={classes.profileStepHeader}>
            <LinkedInIcon className={classes.linkedInStepIcon} />
            <Typography variant="subtitle2" className={classes.profileStepTitle}>
              {locale === 'de'
                ? 'Schritt 2: LinkedIn verbinden'
                : t('linkedInStep2Title', 'Step 2: Connect LinkedIn')}
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" className={classes.profileStepText}>
            {locale === 'de'
              ? 'Deine Account-Daten sind vollständig ✓ — als Nächstes verbindest du deinen LinkedIn-Account, damit Kampagnen laufen können. Klicke hier, um die Zugangsdaten sicher einzugeben.'
              : 'Your account details are complete ✓ — next, connect your LinkedIn account so campaigns can run. Click here to enter your credentials securely.'}
          </Typography>
        </Paper>
      )}

      {!profileIncomplete && !linkedInStepPending && (
        <Box className={classes.suggestionsGrid}>
          {suggestedActions.map((action: SuggestedAction) => {
            const localizedMessage = action.localizedMessages?.[locale] || action.message;
            const localizedLabel = action.localizedLabels?.[locale] || action.label;
            return (
              <Paper
                key={action.id}
                className={classes.suggestionCard}
                onClick={() => onSuggestedAction(localizedMessage)}
                elevation={0}
              >
                <Box className={classes.suggestionIcon}>{getIcon(action.icon)}</Box>
                <Typography variant="body2" className={classes.suggestionLabel}>
                  {localizedLabel}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      )}

      {!profileIncomplete && !linkedInStepPending && (
      <Box className={classes.capabilities}>
        <Typography variant="caption" color="textSecondary" className={classes.capabilitiesTitle}>
          {locale === 'de' ? 'Wobei ich dir helfen kann' : t('whatICanDo', 'What I can help you with')}:
        </Typography>
        <Box className={classes.capabilitiesList}>
          <Typography variant="caption" color="textSecondary">
            • {locale === 'de' ? 'LinkedIn Accounts hinzufügen und verwalten' : t('capability1', 'Add and manage LinkedIn accounts')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            • {locale === 'de' ? 'Recruiting- und Sales-Kampagnen erstellen' : t('capability2', 'Create recruiting and sales campaigns')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            • {locale === 'de' ? 'Kampagnen-Statistiken anzeigen und erklären' : t('capability3', 'View and explain campaign statistics')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            • {locale === 'de' ? 'Account- und Kampagnen-Probleme lösen' : t('capability4', 'Troubleshoot account and campaign issues')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            • {locale === 'de' ? 'Fragen zur Plattform beantworten' : t('capability5', 'Answer questions about the platform')}
          </Typography>
        </Box>
      </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  welcomeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 24px 24px',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  logoContainer: {
    marginBottom: 16,
    flexShrink: 0,
  },
  logoCircle: {
    width: 56,
    height: 56,
    borderRadius: 16,
    background: dc.gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 6px 24px ${dc.primary}44`,
  },
  logoIcon: {
    fontSize: '2rem',
    color: theme.palette.primary.contrastText,
  },
  title: {
    fontWeight: 700,
    marginBottom: 6,
    color: theme.palette.text.primary,
    fontSize: '1.6rem',
  },
  subtitle: {
    maxWidth: 400,
    marginBottom: 24,
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  profileStepBox: {
    width: '100%',
    maxWidth: 400,
    padding: '14px 16px',
    borderRadius: 14,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: `${theme.palette.primary.main}0F`,
    cursor: 'pointer',
    textAlign: 'left',
    marginBottom: 24,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}1F`,
      transform: 'translateY(-2px)',
      boxShadow: `0 10px 26px ${theme.palette.primary.main}26`,
    },
  },
  profileStepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  profileStepIcon: {
    color: theme.palette.primary.main,
    fontSize: '1.4rem',
  },
  linkedInStepIcon: {
    color: '#0A66C2',
    fontSize: '1.5rem',
  },
  profileStepTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  profileStepText: {
    display: 'block',
    lineHeight: 1.5,
  },
  suggestionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 10,
    width: '100%',
    maxWidth: 400,
    marginBottom: 24,
  },
  suggestionCard: {
    padding: '12px 16px',
    borderRadius: 14,
    border: `1px solid ${dc.border}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    backgroundColor: dc.surface,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}0A`,
      transform: 'translateY(-2px)',
      boxShadow: `0 10px 26px ${theme.palette.primary.main}26`,
    },
  },
  suggestionIcon: {
    color: theme.palette.primary.main,
    '& svg': {
      fontSize: '1.5rem',
    },
  },
  suggestionLabel: {
    fontWeight: 500,
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
  },
  capabilities: {
    backgroundColor: dc.bg,
    border: `1px solid ${dc.border}`,
    padding: '12px 16px',
    borderRadius: 12,
    width: '100%',
    maxWidth: 400,
    flexShrink: 0,
  },
  capabilitiesTitle: {
    fontWeight: 600,
    display: 'block',
    marginBottom: 8,
    color: theme.palette.text.primary,
  },
  capabilitiesList: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    textAlign: 'left',
    '& .MuiTypography-root': {
      color: theme.palette.text.secondary,
    },
  },
}));

export default ChatWelcome;

